import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { AuthorizationService } from './auth.service';

export interface ConfigurationAccessGroup {
  uuid: string | null; //this should be generated server side vs sent
  name: string;
  description: string;
}

export interface ConfigurationAccessGroupComplete {
  group: ConfigurationAccessGroup;
  userIds: number[];
  userGroupIds: number[];
  configurationUuids: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ConfigurationAccessService {
  private baseUrl = '/configuration-access';

  constructor(private http: HttpClient, private authService: AuthorizationService) { }

  /**
   * Gets all configuration access groups
   * @returns Observable of ConfigurationAccessGroup array
   */
  getAllAccessGroups(): Observable<ConfigurationAccessGroup[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<ConfigurationAccessGroup[]>(`${this.baseUrl}/groups`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets an access group by UUID
   * @param uuid Access group UUID
   * @returns Observable of ConfigurationAccessGroup
   */
  getAccessGroupByUuid(uuid: string): Observable<ConfigurationAccessGroup> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<ConfigurationAccessGroup>(`${this.baseUrl}/groups/${uuid}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets an access group by name
   * @param name Access group name
   * @returns Observable of ConfigurationAccessGroup
   */
  getAccessGroupByName(name: string): Observable<ConfigurationAccessGroup> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<ConfigurationAccessGroup>(`${this.baseUrl}/groups/name/${name}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Creates a new access group
   * @param group Access group to create
   * @returns Observable of void
   */
  createAccessGroup(group: ConfigurationAccessGroup): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.post<void>(`${this.baseUrl}/groups`, group);
    } else {
      return EMPTY;
    }
  }

  /**
   * Creates a new access group with associated users, user groups, and configurations
   * @param completeGroup Complete access group data including associations
   * @returns Observable of the created ConfigurationAccessGroup
   */
  createCompleteAccessGroup(completeGroup: ConfigurationAccessGroupComplete): Observable<ConfigurationAccessGroup> {
    if (this.authService.isLoggedIn()) {
      return this.http.post<ConfigurationAccessGroup>(`${this.baseUrl}/groups/complete`, completeGroup);
    } else {
      return EMPTY;
    }
  }

  /**
   * Updates an existing access group
   * @param group Updated access group
   * @returns Observable of void
   */
  updateAccessGroup(group: ConfigurationAccessGroup): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.put<void>(`${this.baseUrl}/groups`, group);
    } else {
      return EMPTY;
    }
  }

  /**
   * Updates an access group with all its associations
   * @param completeGroup Complete access group data including associations
   * @returns Observable of the updated ConfigurationAccessGroup
   */
  updateCompleteAccessGroup(completeGroup: ConfigurationAccessGroupComplete): Observable<ConfigurationAccessGroup> {
    if (this.authService.isLoggedIn()) {
      return this.http.put<ConfigurationAccessGroup>(`${this.baseUrl}/groups/complete`, completeGroup);
    } else {
      return EMPTY;
    }
  }

  /**
   * Deletes an access group
   * @param uuid Access group UUID
   * @returns Observable of void
   */
  deleteAccessGroup(uuid: string): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.delete<void>(`${this.baseUrl}/groups/${uuid}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Adds a user to an access group
   * @param uuid Access group UUID
   * @param userId User ID
   * @returns Observable of void
   */
  addUserToAccessGroup(uuid: string, userId: number): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.post<void>(`${this.baseUrl}/groups/${uuid}/users/${userId}`, {});
    } else {
      return EMPTY;
    }
  }

  /**
   * Removes a user from an access group
   * @param uuid Access group UUID
   * @param userId User ID
   * @returns Observable of void
   */
  removeUserFromAccessGroup(uuid: string, userId: number): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.delete<void>(`${this.baseUrl}/groups/${uuid}/users/${userId}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all users in an access group
   * @param uuid Access group UUID
   * @returns Observable of user IDs array
   */
  getUsersInAccessGroup(uuid: string): Observable<number[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<number[]>(`${this.baseUrl}/groups/${uuid}/users`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all access groups for a user
   * @param userId User ID
   * @returns Observable of ConfigurationAccessGroup array
   */
  getAccessGroupsForUser(userId: number): Observable<ConfigurationAccessGroup[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<ConfigurationAccessGroup[]>(`${this.baseUrl}/users/${userId}/groups`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Adds a user group to an access group
   * @param uuid Access group UUID
   * @param userGroupId User group ID
   * @returns Observable of void
   */
  addUserGroupToAccessGroup(uuid: string, userGroupId: number): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.post<void>(`${this.baseUrl}/groups/${uuid}/usergroups/${userGroupId}`, {});
    } else {
      return EMPTY;
    }
  }

  /**
   * Removes a user group from an access group
   * @param uuid Access group UUID
   * @param userGroupId User group ID
   * @returns Observable of void
   */
  removeUserGroupFromAccessGroup(uuid: string, userGroupId: number): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.delete<void>(`${this.baseUrl}/groups/${uuid}/usergroups/${userGroupId}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all user groups in an access group
   * @param uuid Access group UUID
   * @returns Observable of user group IDs array
   */
  getUserGroupsInAccessGroup(uuid: string): Observable<number[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<number[]>(`${this.baseUrl}/groups/${uuid}/usergroups`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all access groups for a user group
   * @param userGroupId User group ID
   * @returns Observable of ConfigurationAccessGroup array
   */
  getAccessGroupsForUserGroup(userGroupId: number): Observable<ConfigurationAccessGroup[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<ConfigurationAccessGroup[]>(`${this.baseUrl}/usergroups/${userGroupId}/groups`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Adds a configuration to an access group
   * @param uuid Access group UUID
   * @param configurationUuid Configuration UUID
   * @returns Observable of void
   */
  addConfigurationToAccessGroup(uuid: string, configurationUuid: string): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.post<void>(`${this.baseUrl}/groups/${uuid}/configurations/${configurationUuid}`, {});
    } else {
      return EMPTY;
    }
  }

  /**
   * Removes a configuration from an access group
   * @param uuid Access group UUID
   * @param configurationUuid Configuration UUID
   * @returns Observable of void
   */
  removeConfigurationFromAccessGroup(uuid: string, configurationUuid: string): Observable<void> {
    if (this.authService.isLoggedIn()) {
      return this.http.delete<void>(`${this.baseUrl}/groups/${uuid}/configurations/${configurationUuid}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all configurations in an access group
   * @param uuid Access group UUID
   * @returns Observable of configuration UUIDs array
   */
  getConfigurationsInAccessGroup(uuid: string): Observable<string[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<string[]>(`${this.baseUrl}/groups/${uuid}/configurations`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Gets all access groups for a configuration
   * @param configurationUuid Configuration UUID
   * @returns Observable of ConfigurationAccessGroup array
   */
  getAccessGroupsForConfiguration(configurationUuid: string): Observable<ConfigurationAccessGroup[]> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<ConfigurationAccessGroup[]>(`${this.baseUrl}/configurations/${configurationUuid}/groups`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Checks if a user has access to a configuration
   * @param userId User ID
   * @param configurationUuid Configuration UUID
   * @returns Observable of boolean indicating access
   */
  userHasAccessToConfiguration(userId: number, configurationUuid: string): Observable<boolean> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<boolean>(`${this.baseUrl}/access/user/${userId}/configuration/${configurationUuid}`);
    } else {
      return EMPTY;
    }
  }

  /**
   * Checks if a user group has access to a configuration
   * @param userGroupId User group ID
   * @param configurationUuid Configuration UUID
   * @returns Observable of boolean indicating access
   */
  userGroupHasAccessToConfiguration(userGroupId: number, configurationUuid: string): Observable<boolean> {
    if (this.authService.isLoggedIn()) {
      return this.http.get<boolean>(`${this.baseUrl}/access/usergroup/${userGroupId}/configuration/${configurationUuid}`);
    } else {
      return EMPTY;
    }
  }
}